/**
 * What a criterion says about itself, so a value on it reads later.
 *
 * Owner ruling (Wes, 2026-10-28, the installed-offense vocabulary): "A Criterion
 * names what it measures, its beneficiary, its baseline, and its scale." The test
 * is the stranger test: someone who was not there reads "Impact: 8" and knows
 * what the 8 measured, for whom, and against what. The three parts live on
 * `public.criteria` (migration 967); a criterion missing any of them is warned
 * about, never blocked.
 *
 * Pure: no store, no client. The criteria store writes the parts; this file
 * says which are missing and how they read.
 */

export type CriterionReadingField = 'beneficiary' | 'baseline' | 'scale';

export interface CriterionReading {
  beneficiary: string | null;
  baseline: string | null;
  scale: string | null;
}

/** The three parts, in the order they are asked and read, with their labels and one-line hints. */
export const CRITERION_READING_PARTS: ReadonlyArray<{
  field: CriterionReadingField;
  label: string;
  hint: string;
  placeholder: string;
}> = [
  {
    field: 'beneficiary',
    label: 'For whom',
    hint: 'Who gains when this value is high: a customer, a team, the company.',
    placeholder: 'e.g. the Trinity Valley Health account',
  },
  {
    field: 'baseline',
    label: 'Against what',
    hint: 'What a value is compared with: today, last quarter, a competitor.',
    placeholder: 'e.g. their current churn risk',
  },
  {
    field: 'scale',
    label: 'Scale',
    hint: 'What a 1 and a 10 mean (or the lowest and highest point on its scale).',
    placeholder: 'e.g. 1 = no effect on renewal, 10 = renewal saved',
  },
];

export const CRITERION_INCOMPLETE_WARNING =
  'This criterion will not read later: say for whom, against what, and the scale.';

function clean(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const t = value.trim();
  return t === '' ? null : t;
}

/** The reading of a criteria row (or nothing): blank parts read as missing. */
export function criterionReadingOf(
  row: { beneficiary?: string | null; baseline?: string | null; scale?: string | null } | null | undefined,
): CriterionReading {
  return {
    beneficiary: clean(row?.beneficiary),
    baseline: clean(row?.baseline),
    scale: clean(row?.scale),
  };
}

/** The parts a reader would still have to guess, in asking order. */
export function missingCriterionReadingParts(reading: CriterionReading): CriterionReadingField[] {
  return CRITERION_READING_PARTS.map((p) => p.field).filter((f) => reading[f] == null);
}

/** The warning line for a criterion missing any part, or null when it reads on its own. */
export function criterionIncompleteWarning(reading: CriterionReading): string | null {
  return missingCriterionReadingParts(reading).length > 0 ? CRITERION_INCOMPLETE_WARNING : null;
}

/** The parts that are filled, as label/text pairs in reading order. */
export function criterionReadingLines(reading: CriterionReading): { label: string; text: string }[] {
  return CRITERION_READING_PARTS.flatMap((p) => {
    const text = reading[p.field];
    return text ? [{ label: p.label, text }] : [];
  });
}

/** One line for beside a label: "For whom: … · Against what: … · Scale: …", or null when nothing is said. */
export function criterionReadingSummary(reading: CriterionReading): string | null {
  const lines = criterionReadingLines(reading);
  return lines.length ? lines.map((l) => `${l.label}: ${l.text}`).join(' · ') : null;
}

/**
 * The criteria row that defines an attribute's criterion. A criterion is an
 * attribute selected for evaluation (canon 01): its row points at the attribute
 * by `attributeKey` (older rows only by `key`). The workspace's own row wins over
 * a shared one (no workspace), because a workspace says for whom it measures;
 * another workspace's row is never read here.
 */
export function criterionForAttribute<
  T extends { key: string; attributeKey?: string | null; workspaceId?: string | null },
>(criteria: readonly T[], attributeKey: string, workspaceId?: string | null): T | undefined {
  const matches = criteria.filter(
    (c) =>
      (c.attributeKey === attributeKey || c.key === attributeKey) &&
      (!workspaceId || c.workspaceId == null || c.workspaceId === workspaceId),
  );
  return matches.find((c) => !!workspaceId && c.workspaceId === workspaceId) ?? matches[0];
}
